/**
 * Qonto API service — communicates with the backend worker
 * All Qonto OAuth tokens stay server-side; the CRM sends
 * authenticated requests through the proxy endpoint.
 */

const API_BASE = import.meta.env.VITE_EMAIL_API_URL || 'https://api.lessablesweb.fr'

/** Thrown when the Qonto OAuth token is expired or revoked (401) */
export class QontoAuthError extends Error {
  constructor(message = 'Token Qonto expiré ou invalide') {
    super(message)
    this.name = 'QontoAuthError'
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { supabase } = await import('@/lib/supabase')
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Non authentifié')
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  }
}

// ============================================
// OAuth Connection
// ============================================

/**
 * Get the OAuth authorization URL from the backend,
 * then the CRM redirects the browser to Qonto.
 */
export async function getQontoAuthUrl(): Promise<string> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/auth`, {
    method: 'POST',
    headers,
  })
  const json = await res.json()
  if (!json.success) throw new Error(json.error?.message || 'Failed to get auth URL')
  return json.data.url
}

/**
 * Check Qonto connection status
 */
export async function getQontoStatus(): Promise<{
  connected: boolean
  sandbox: boolean
  connected_at?: string
  expires_at?: number
  scopes?: string[]
}> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/status`, { headers })
  if (res.status === 401) throw new QontoAuthError()
  const json = await res.json()
  if (!json.success) throw new Error(json.error?.message || 'Failed to get status')
  return json.data
}

/**
 * Disconnect Qonto
 */
export async function disconnectQonto(): Promise<void> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/disconnect`, {
    method: 'POST',
    headers,
  })
  const json = await res.json()
  if (!json.success) throw new Error(json.error?.message || 'Failed to disconnect')
}

/**
 * Force refresh the access token
 */
export async function refreshQontoToken(): Promise<{ expires_at: number; scope: string }> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/refresh`, {
    method: 'POST',
    headers,
  })
  const json = await res.json()
  if (!json.success) throw new Error(json.error?.message || 'Failed to refresh')
  return json.data
}

// ============================================
// Qonto API Proxy
// ============================================

/**
 * Generic Qonto API call via the backend proxy
 * Tokens never leave the server — the CRM sends the request shape,
 * the worker attaches the OAuth bearer token.
 */
export async function qontoApiCall<T = unknown>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
    body?: unknown
    params?: Record<string, string>
  } = {}
): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/api`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      path,
      method: options.method || 'GET',
      body: options.body,
      params: options.params,
    }),
  })

  // 401 from the worker means the Qonto token is dead
  if (res.status === 401) throw new QontoAuthError()

  const json = await res.json()
  if (!json.success) {
    // The worker may proxy a 401 as { success: false } with a message
    const msg: string = json.error?.message || ''
    if (msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('token') || msg.includes('401')) {
      throw new QontoAuthError(msg)
    }
    throw new Error(msg || 'Qonto API call failed')
  }
  return json.data as T
}

// ============================================
// Convenience wrappers (typed Qonto endpoints)
// ============================================

/** Get organization info and bank accounts */
export function getOrganization() {
  return qontoApiCall<{ organization: unknown }>('/organization')
}

/** List transactions */
export function getTransactions(params?: Record<string, string>) {
  return qontoApiCall<{ transactions: unknown[] }>('/transactions', { params })
}

/** List clients */
export function getClients(params?: Record<string, string>) {
  return qontoApiCall<{ clients: unknown[] }>('/clients', { params })
}

/** Create a client */
export function createClient(data: unknown) {
  return qontoApiCall<{ client: unknown }>('/clients', { method: 'POST', body: data })
}

/** Create a client invoice */
export function createInvoice(data: unknown) {
  return qontoApiCall<{ client_invoice: unknown }>('/client_invoices', { method: 'POST', body: data })
}

/** Finalize (send) an invoice */
export function finalizeInvoice(invoiceId: string) {
  return qontoApiCall<{ client_invoice: unknown }>(`/client_invoices/${invoiceId}/finalize`, { method: 'POST' })
}

/** List invoices */
export function getInvoices(params?: Record<string, string>) {
  return qontoApiCall<{ client_invoices: unknown[] }>('/client_invoices', { params })
}

/** Create a quote */
export function createQuote(data: unknown) {
  return qontoApiCall<{ quote: unknown }>('/quotes', { method: 'POST', body: data })
}

/** List quotes */
export function getQuotes(params?: Record<string, string>) {
  return qontoApiCall<{ quotes: unknown[] }>('/quotes', { params })
}

/** Send a quote via email */
export function sendQuote(quoteId: string, data: { recipients: string[] }) {
  return qontoApiCall<{ quote: unknown }>(`/quotes/${quoteId}/send`, { method: 'POST', body: data })
}

/** Create a payment link */
export function createPaymentLink(data: unknown) {
  return qontoApiCall<{ payment_link: unknown }>('/payment_links', { method: 'POST', body: data })
}

/** List payment links */
export function getPaymentLinks(params?: Record<string, string>) {
  return qontoApiCall<{ payment_links: unknown[] }>('/payment_links', { params })
}

/** Deactivate a payment link */
export function deactivatePaymentLink(linkId: string) {
  return qontoApiCall<{ payment_link: unknown }>(`/payment_links/${linkId}/deactivate`, { method: 'PATCH' })
}

/**
 * Download a Qonto attachment (PDF) as a Blob
 * The PDF is streamed through the worker to keep tokens server-side.
 */
export async function downloadQontoAttachment(attachmentId: string): Promise<Blob> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/qonto/attachment/${attachmentId}`, { headers })
  if (!res.ok) {
    const json = await res.json().catch(() => null)
    throw new Error(json?.error?.message || `Erreur téléchargement attachment (${res.status})`)
  }
  return res.blob()
}
